import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';

const TODAY = new Date().toISOString().split('T')[0];
const EMPTY_ITEM = { tiep_id:'', qty_utilized:'', custom_grn_liability:'' };
const emptyForm = () => ({
  export_entry_no:'', header_date:'', sign_date: TODAY,
  office_code:'CBEX1', our_reference:'', importer_code:'1025458857000',
  export_product:'', export_hs_code:'', export_qty:'', teip_approval_no:'5701420',
  auth_signatory:'', designation:'Admin Manager',
});

const fmtDate = d => { if(!d) return ''; try { return new Date(d).toLocaleDateString('en-GB'); } catch { return d; } };
const fmtNum = (n,dp=2) => { const v=parseFloat(n); return isNaN(v) ? '' : v.toLocaleString(undefined,{minimumFractionDigits:dp,maximumFractionDigits:dp}); };

export default function MudFormPage() {
  const toast = useToast();
  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [tiepGoods, setTiepGoods] = useState([]);
  const [issues, setIssues] = useState([]);
  const [savedForms, setSavedForms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('form');
  const [grn, setGrn] = useState({});
  const [grnSaving, setGrnSaving] = useState({});
  const printRef = useRef();

  useEffect(() => {
    api.get('/tiep_goods/list.php').then(r => { if(r.data.success) setTiepGoods(r.data.data); });
    api.get('/issue_goods/list.php').then(r => { if(r.data.success) setIssues(r.data.data); });
    loadSaved();
  }, []);

  const loadSaved = () => api.get('/mud_form/list.php').then(r => { if(r.data.success) setSavedForms(r.data.data); });

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const usedCusdecs = new Set(savedForms.map(s => s.export_entry_no));
  const availableCusdecs = [...new Set(issues.map(i => i.export_cusdec))].filter(c => !usedCusdecs.has(c)).sort();

  const handleExportCusdec = val => {
    const matches = issues.filter(i => i.export_cusdec === val);
    let headerDate = '';
    if (matches.length) {
      const sorted = [...matches].sort((a,b) => new Date(a.date)-new Date(b.date));
      headerDate = sorted[0].date?.split('T')[0] || '';
    }
    setForm(f => ({ ...f, export_entry_no: val, header_date: headerDate, sign_date: TODAY }));

    if (matches.length) {
      setItems(matches.map(issue => {
        const t = tiepGoods.find(tg => tg.cusdec_no === issue.cusdec_no);
        return {
          tiep_id: t ? String(t.id) : '',
          qty_utilized: String(issue.quantity),
          custom_grn_liability: '',
          goods: t?.goods||'', cusdec_no: issue.cusdec_no, hs_code: issue.hs_code, units: issue.units,
          tiep_date: t?.date||'', tiep_qty: t ? +t.qty : 0, tiep_liability: t ? +t.liability : 0,
        };
      }));
    } else setItems([{ ...EMPTY_ITEM }]);
  };

  const addItem = () => setItems(it => [...it, { ...EMPTY_ITEM }]);
  const removeItem = idx => setItems(it => it.filter((_,i) => i!==idx));

  const setItem = (idx,k,v) => {
    setItems(it => it.map((row,i) => {
      if (i!==idx) return row;
      const u = { ...row, [k]: v };
      if (k==='tiep_id') {
        const e = tiepGoods.find(t => String(t.id)===v);
        if (e) Object.assign(u, { goods:e.goods, cusdec_no:e.cusdec_no, hs_code:e.hs_code, units:e.units, tiep_date:e.date, tiep_qty:+e.qty, tiep_liability:+e.liability });
      }
      return u;
    }));
  };

  const calcAmt  = it => (!it.tiep_liability||!it.tiep_qty||!it.qty_utilized) ? 0 : (it.tiep_liability/it.tiep_qty)*it.qty_utilized;
  const calcDiff = it => calcAmt(it) - (parseFloat(it.custom_grn_liability)||0);
  const totalAmt  = items.reduce((a,it) => a+calcAmt(it), 0);
  const totalDiff = items.reduce((a,it) => a+calcDiff(it), 0);

  const handleSave = async () => {
    if (!form.export_entry_no) { toast('Export Entry No required', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        export_entry_no: form.export_entry_no, date: form.header_date||TODAY,
        office_code: form.office_code, our_reference: form.our_reference, importer_code: form.importer_code,
        export_product: form.export_product, export_hs_code: form.export_hs_code,
        export_qty: +form.export_qty||0, teip_approval_no: form.teip_approval_no,
        auth_signatory: form.auth_signatory, designation: form.designation, sign_date: form.sign_date,
        items: items.filter(it => it.tiep_id).map(it => ({ tiep_id:+it.tiep_id, qty_utilized:+it.qty_utilized||0, custom_grn_liability:+it.custom_grn_liability||0 })),
      };
      const { data } = await api.post('/mud_form/create.php', payload);
      if (data.success) { toast('MUD Form saved!'); setForm(emptyForm()); setItems([{...EMPTY_ITEM}]); loadSaved(); setTab('saved'); }
      else toast(data.error||'Save failed', 'error');
    } catch { toast('Network error', 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteSaved = async id => {
    if (!window.confirm('Delete this MUD form?')) return;
    await api.post('/mud_form/delete.php', { id });
    loadSaved(); toast('Deleted');
  };

  const handleSaveGrn = async (formId, itemId) => {
    setGrnSaving(s => ({ ...s, [itemId]: true }));
    try {
      const val = grn[itemId] ?? '0';
      const { data } = await api.post('/mud_form/update_grn.php', { mud_form_id: formId, item_id: itemId, custom_grn_liability: +val||0 });
      if (data.success) { toast('GRN Liability saved'); loadSaved(); setGrn(g => { const n={...g}; delete n[itemId]; return n; }); }
      else toast(data.error||'Failed', 'error');
    } catch { toast('Network error', 'error'); }
    finally { setGrnSaving(s => ({ ...s, [itemId]: false })); }
  };

  const handleLoad = sf => {
    setForm({
      export_entry_no: sf.export_entry_no, header_date: sf.date?.split('T')[0]||'', sign_date: TODAY,
      office_code: sf.office_code||'CBEX1', our_reference: sf.our_reference||'', importer_code: sf.importer_code||'1025458857000',
      export_product: sf.export_product||'', export_hs_code: sf.export_hs_code||'', export_qty: String(sf.export_qty||''),
      teip_approval_no: sf.teip_approval_no||'5701420', auth_signatory: sf.auth_signatory||'', designation: sf.designation||'Admin Manager',
    });
    if (sf.items?.length) setItems(sf.items.map(it => ({
      tiep_id:String(it.tiep_id), qty_utilized:String(it.qty_utilized), custom_grn_liability:String(it.custom_grn_liability||''),
      goods:it.goods, cusdec_no:it.cusdec_no, hs_code:it.hs_code, units:it.units, tiep_date:it.tiep_date, tiep_qty:it.tiep_qty, tiep_liability:it.tiep_liability,
    })));
    setTab('form');
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const win = window.open('', '_blank', 'width=850,height=1200');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>MUD Form - ${form.export_entry_no}</title>
    <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Times New Roman',Times,serif;font-size:11pt;color:#000;padding:15mm 18mm}table{width:100%;border-collapse:collapse}th,td{border:1px solid #000;padding:3px 5px}thead th{font-weight:bold;text-align:center;background:#f0f0f0;font-size:8.5pt}tbody td{font-size:9pt}@page{size:A4;margin:15mm}</style>
    </head><body>${printRef.current.innerHTML}</body></html>`);
    win.document.close(); win.focus();
    setTimeout(() => win.print(), 600);
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">MUD Form</div><div className="page-sub">Details of Raw Material Utilised – for guarantee cancellation</div></div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : '💾 Save Form'}</button>
          <button className="btn btn-primary" onClick={handlePrint}>⎙ Print A4</button>
        </div>
      </div>

      <div style={{ background:'#fff', borderBottom:'1px solid var(--border)', padding:'0 24px' }} className="tabs">
        <button className={`tab ${tab==='form'?'active':''}`} onClick={() => setTab('form')}>Create / Edit Form</button>
        <button className={`tab ${tab==='saved'?'active':''}`} onClick={() => setTab('saved')}>Saved Forms ({savedForms.length})</button>
      </div>

      {tab==='form' && (
        <div className="page-body" style={{ display:'flex', flexDirection:'column', gap:18 }}>
          {/* Header */}
          <div className="card">
            <div className="card-header"><span className="card-title">📄 Form Header</span></div>
            <div className="card-body">
              <div className="form-grid cols-3">
                <div className="form-group">
                  <label className="lbl">Export Entry No *</label>
                  <select className="input" value={form.export_entry_no} onChange={e => handleExportCusdec(e.target.value)}>
                    <option value="">-- Select Export CusDec --</option>
                    {availableCusdecs.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {usedCusdecs.size > 0 && <p style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>Already saved: {[...usedCusdecs].join(', ')}</p>}
                </div>
                <div className="form-group">
                  <label className="lbl">Header Date (from Issue Record)</label>
                  <input className="input" readOnly value={form.header_date ? fmtDate(form.header_date) : ''} placeholder="Auto-filled on selection" />
                </div>
                <div className="form-group"><label className="lbl">Office Code</label><input className="input" value={form.office_code} onChange={set('office_code')} /></div>
                <div className="form-group"><label className="lbl">Our Reference</label><input className="input" value={form.our_reference} onChange={set('our_reference')} placeholder="e.g. REF001" /></div>
                <div className="form-group"><label className="lbl">Importer Code</label><input className="input" value={form.importer_code} onChange={set('importer_code')} /></div>
                <div className="form-group"><label className="lbl">TEIP Approval No</label><input className="input" value={form.teip_approval_no} onChange={set('teip_approval_no')} /></div>
                <div className="form-group"><label className="lbl">Export Product</label><input className="input" value={form.export_product} onChange={set('export_product')} placeholder="e.g. Organic Pineapple Chunks" /></div>
                <div className="form-group"><label className="lbl">Export HS Code</label><input className="input" value={form.export_hs_code} onChange={set('export_hs_code')} placeholder="e.g. 20082090" /></div>
                <div className="form-group"><label className="lbl">Export Quantity</label><input className="input" type="number" value={form.export_qty} onChange={set('export_qty')} placeholder="e.g. 23040" /></div>
                <div className="form-group"><label className="lbl">Authorised Signatory</label><input className="input" value={form.auth_signatory} onChange={set('auth_signatory')} placeholder="e.g. M A Buddhika" /></div>
                <div className="form-group"><label className="lbl">Designation</label><input className="input" value={form.designation} onChange={set('designation')} /></div>
                <div className="form-group"><label className="lbl">Signature Date (editable)</label><input className="input" type="date" value={form.sign_date} onChange={set('sign_date')} /></div>
              </div>
            </div>
          </div>

          {/* Raw materials */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">📦 Raw Materials Utilised</span>
              <button className="btn btn-primary btn-sm" onClick={addItem}>＋ Add Row</button>
            </div>
            <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {items.map((item, idx) => {
                const amt = calcAmt(item), diff = calcDiff(item);
                return (
                  <div key={idx} style={{ border:'1px solid var(--border)', borderRadius:12, padding:16, background:'var(--light)', position:'relative' }}>
                    {items.length>1 && <button onClick={() => removeItem(idx)} style={{ position:'absolute', top:8, right:8, background:'none', border:'none', color:'var(--danger)', cursor:'pointer', fontSize:16 }}>✕</button>}
                    <p style={{ fontSize:11, fontWeight:700, color:'var(--text2)', textTransform:'uppercase', marginBottom:10 }}>Row {idx+1}</p>
                    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:12 }}>
                      <div className="form-group">
                        <label className="lbl">TIEP Entry (CUSDEC)</label>
                        <select className="input" value={item.tiep_id} onChange={e => setItem(idx,'tiep_id',e.target.value)}>
                          <option value="">-- Select TIEP Entry --</option>
                          {tiepGoods.map(t => <option key={t.id} value={t.id}>{t.cusdec_no} | {t.name} | {t.goods}</option>)}
                        </select>
                      </div>
                      <div className="form-group"><label className="lbl">QTY Utilized</label><input className="input" type="number" value={item.qty_utilized} onChange={e => setItem(idx,'qty_utilized',e.target.value)} placeholder="Enter qty" /></div>
                      <div className="form-group"><label className="lbl">Custom GRN Liability</label><input className="input" type="number" step="0.01" value={item.custom_grn_liability} onChange={e => setItem(idx,'custom_grn_liability',e.target.value)} placeholder="0.00" /></div>
                    </div>
                    {item.tiep_id && (
                      <div style={{ marginTop:10, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, fontSize:11 }}>
                        <div style={{ background:'#fff', borderRadius:8, padding:'8px 10px', border:'1px solid var(--border)' }}><span style={{ color:'var(--text2)', display:'block' }}>CUSDEC / Date</span><strong>{item.cusdec_no} / {fmtDate(item.tiep_date)}</strong></div>
                        <div style={{ background:'#fff', borderRadius:8, padding:'8px 10px', border:'1px solid var(--border)' }}><span style={{ color:'var(--text2)', display:'block' }}>HS / UOM</span><strong>{item.hs_code} / {item.units}</strong></div>
                        <div style={{ background:'#fff', borderRadius:8, padding:'8px 10px', border:'1px solid var(--border)' }}><span style={{ color:'var(--text2)', display:'block' }}>AMT for Cancellation</span><strong style={{ color:'var(--teal)' }}>{fmtNum(amt)}</strong></div>
                        <div style={{ background: diff>=0 ? '#f0fdf4' : '#fef2f2', borderRadius:8, padding:'8px 10px', border:`1px solid ${diff>=0?'#86efac':'#fca5a5'}` }}><span style={{ color:'var(--text2)', display:'block' }}>Liability Diff</span><strong style={{ color: diff>=0?'#15803d':'var(--danger)' }}>{fmtNum(diff)}</strong></div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ display:'flex', justifyContent:'flex-end', gap:24, paddingTop:10, borderTop:'1px solid var(--border)', fontSize:13 }}>
                <div><span style={{ color:'var(--text2)' }}>Total AMT: </span><strong style={{ color:'var(--teal)' }}>{fmtNum(totalAmt)}</strong></div>
                <div><span style={{ color:'var(--text2)' }}>Total Diff: </span><strong style={{ color: totalDiff>=0?'#15803d':'var(--danger)' }}>{fmtNum(totalDiff)}</strong></div>
              </div>
            </div>
          </div>

          {/* A4 Preview */}
          <div className="card">
            <div className="card-header"><span className="card-title">👁 A4 Print Preview</span></div>
            <div style={{ padding:16, background:'#e5e7eb', overflow:'auto', maxHeight:800 }}>
              <div style={{ background:'#fff', maxWidth:660, margin:'0 auto', padding:'22px 26px', boxShadow:'0 4px 16px rgba(0,0,0,.15)' }}>
                <div ref={printRef}>
                  <div style={{ textAlign:'center', fontSize:10, marginBottom:6 }}>A</div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                    <div style={{ fontSize:10 }}><strong>REF :</strong>&nbsp;{form.our_reference}</div>
                    <div style={{ fontSize:10, lineHeight:1.8 }}>
                      {[['EXPORT ENTRY NO', form.export_entry_no],['DATE', fmtDate(form.header_date)],['OFFICE CODE', form.office_code],['OUR REFERENCE', form.our_reference]].map(([l,v]) => (
                        <div key={l} style={{ display:'flex', gap:8 }}><span style={{ minWidth:110 }}><strong>{l}</strong></span><span>{v}</span></div>
                      ))}
                    </div>
                  </div>
                  <div style={{ textAlign:'center', fontWeight:'bold', fontSize:14, textDecoration:'underline', textTransform:'uppercase', margin:'10px 0 3px' }}>DETAILS OF RAW MATERIAL UTILISED</div>
                  <div style={{ textAlign:'center', fontSize:10, marginBottom:12 }}>(for guarantee cancellation)</div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, marginBottom:4 }}>
                    <span><strong>CODE OF THE IMPORTER:</strong>&nbsp;{form.importer_code}</span>
                    <span style={{ textAlign:'left', minWidth:220 }}><strong>EXPORT PRODUCT :</strong>&nbsp;{form.export_product}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, marginBottom:4 }}>
                    <span></span><span style={{ textAlign:'left', minWidth:220 }}><strong>H. S. CODE:</strong>&nbsp;{form.export_hs_code}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, marginBottom:12 }}>
                    <span><strong>TEIP APPROVAL NO :</strong>&nbsp;{form.teip_approval_no}</span>
                    <span style={{ textAlign:'left', minWidth:220 }}><strong>QUANTITY :</strong>&nbsp;{form.export_qty}</span>
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:9 }}>
                    <thead>
                      <tr><th colSpan={3} style={{ border:'1px solid #000', padding:'4px 6px', background:'#e8e8e8', fontWeight:'bold' }}>RAW MATERIAL UTILISED</th><th colSpan={6} style={{ border:'1px solid #000', padding:'4px 6px', background:'#e8e8e8', fontWeight:'bold' }}>IMPORT GUARANTEE DETAILS</th></tr>
                      <tr>{['DETAILS','CONT.\nRATIO','QTY','IMPORT CUSDEC /\nGRN NO. & DATE','ITEM\nNO.','H.S. CODE','QTY\nUTILIZED','UOM','AMT. FOR\nCANCELLATION'].map((h,i) => <th key={i} style={{ border:'1px solid #000', padding:'3px 4px', textAlign:'center', fontWeight:'bold', whiteSpace:'pre-line', fontSize:8 }}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {items.filter(it => it.tiep_id).map((it,i) => (
                        <tr key={i}>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'left' }}>{it.goods}</td>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'center' }}>1-1</td>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'center' }}>{Number(it.qty_utilized||0).toLocaleString()}</td>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'left' }}>{it.cusdec_no} / {fmtDate(it.tiep_date)}</td>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'center' }}>{i+1}</td>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'center' }}>{it.hs_code}</td>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'center' }}>{Number(it.qty_utilized||0).toLocaleString()}</td>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'center' }}>{it.units}</td>
                          <td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'right' }}>{fmtNum(calcAmt(it))}</td>
                        </tr>
                      ))}
                      {Array.from({ length: Math.max(0,5-items.filter(it=>it.tiep_id).length) }).map((_,i) => (
                        <tr key={`b${i}`}>{Array(9).fill(null).map((__,j) => <td key={j} style={{ border:'1px solid #000', padding:'14px 6px' }}></td>)}</tr>
                      ))}
                      <tr><td colSpan={8} style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'right', fontWeight:'bold' }}>TOTAL</td><td style={{ border:'1px solid #000', padding:'4px 6px', textAlign:'right', fontWeight:'bold' }}>{fmtNum(totalAmt)}</td></tr>
                    </tbody>
                  </table>
                  <div style={{ marginTop:16, fontSize:10 }}>
                    <p>WE HEREBY CERTIFY THAT ALL PARTICULARS FURNISHED HEREIN ARE TRUE AND CORRECT.</p>
                    <div style={{ marginTop:24 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
                        <span><strong>AUTHORIZED SIGNATORY:</strong></span>
                        <span><strong>NAME</strong>&nbsp;&nbsp;{form.auth_signatory}</span>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between' }}>
                        <span><strong>DESIGNATION :</strong>&nbsp;&nbsp;{form.designation}</span>
                        <span><strong>DATE</strong>&nbsp;&nbsp;{fmtDate(form.sign_date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab==='saved' && (
        <div className="page-body">
          {savedForms.length===0
            ? <div className="card" style={{ padding:48, textAlign:'center', color:'var(--text2)' }}>No saved MUD forms yet.</div>
            : <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
              {savedForms.map(sf => (
                <div key={sf.id} className="card">
                  <div className="card-header">
                    <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
                      {[['Export Entry No', sf.export_entry_no],['Date', fmtDate(sf.date)],['Product', sf.export_product||'—']].map(([l,v]) => (
                        <div key={l}><div style={{ fontSize:10, color:'var(--text2)' }}>{l}</div><div style={{ fontWeight:700, fontSize:13 }}>{v}</div></div>
                      ))}
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleLoad(sf)}>📝 Load & Edit</button>
                      <button className="btn-icon danger" onClick={() => handleDeleteSaved(sf.id)}>🗑</button>
                    </div>
                  </div>
                  {sf.items?.length>0 && (
                    <div className="tbl-wrap">
                      <table>
                        <thead><tr>{['Goods','CUSDEC','HS','QTY Utilized','UOM','AMT for Cancel.','Custom GRN Liability','Diff',''].map(h=><th key={h}>{h}</th>)}</tr></thead>
                        <tbody>
                          {sf.items.map(item => {
                            const perUnit = item.tiep_qty ? item.tiep_liability/item.tiep_qty : 0;
                            const amt = perUnit*item.qty_utilized;
                            const grnVal = grn[item.id] !== undefined ? grn[item.id] : String(item.custom_grn_liability||'');
                            const diff = amt - (parseFloat(grnVal)||0);
                            const dirty = grnVal !== String(item.custom_grn_liability||'');
                            return (
                              <tr key={item.id}>
                                <td>{item.goods}</td>
                                <td className="mono">{item.cusdec_no}</td>
                                <td className="mono">{item.hs_code}</td>
                                <td>{Number(item.qty_utilized).toLocaleString()}</td>
                                <td>{item.units}</td>
                                <td style={{ fontWeight:600, color:'var(--teal)' }}>{fmtNum(amt)}</td>
                                <td><input className="input" type="number" step="0.01" style={{ width:120 }} value={grnVal} onChange={e => setGrn(g => ({ ...g, [item.id]: e.target.value }))} placeholder="0.00" /></td>
                                <td><strong style={{ color: diff>=0?'#15803d':'var(--danger)' }}>{fmtNum(diff)}</strong></td>
                                <td>{dirty && <button className="btn btn-primary btn-sm" onClick={() => handleSaveGrn(sf.id,item.id)} disabled={grnSaving[item.id]}>{grnSaving[item.id]?'…':'Save'}</button>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          }
        </div>
      )}
    </div>
  );
}
